import React, {useEffect, useState} from "react";
import {Button, Dropdown, DropdownItemProps, DropdownProps, Form, Header, Icon, Popup, Table} from "semantic-ui-react";
import agent, {TaskListRequest, TaskListResponse} from "../../agent";
import {
    getTaskResultColor,
    getTaskResultDisplayName,
    getTaskStatusColor,
    getTaskStatusDisplayName,
    TaskModel,
    TaskResult,
    TaskResultNames,
    TaskStatus,
    TaskStatusNames,
    TaskTypeModel
} from "./TaskModel";
import {Utils} from "../../helpers/Utils";
import localStyles from "./index.module.css";
import commonStyles from '../common/commonStyles/table.module.css';
import PagingFooter from "../common/PagingFooter";
import TaskDetailsModal from "./TaskDetailsModal";
import {hasRole, UserRole} from "../../helpers/UserRole";
import {getDoctorId} from "../../utils/common";
import {textLabels} from "../../consts/textLabels";

const TaskList = () => {
    const styles = { ...commonStyles, ...localStyles };
    const [loading, setLoading] = useState(true);
    const [request, setRequest] = useState<TaskListRequest>({
        page: 1,
        limit: 10,
        doctorId: getDoctorId() ?? undefined
    });
    const [response, setResponse] = useState<TaskListResponse>();
    const [task, setTask] = useState<TaskModel>();
    const [taskTypeList, setTaskTypeList] = useState<TaskTypeModel[]>([]);
    const [popupVisible, setPopupVisible] = useState(false);

    useEffect(() => {
        agent.TaskProvider.typeList().then((result) => {
            setTaskTypeList(result);
        });
    }, []);

    useEffect(() => {
        load().then();
    }, [request]);

    async function load() {
        setLoading(true);
        setResponse(await agent.TaskProvider.list(request));
        setLoading(false);
    }

    async function loadPage(page: number, limit?: number) {
        setRequest({...request, page: page, limit: limit ?? request.limit});
    }

    function buildFilters(): JSX.Element {
        return (<div className={styles.list}><Form>
            <Form.Group>
                {buildTaskTypeFilter()}
                {buildStatusFilter()}
                {buildResultFilter()}
            </Form.Group>
        </Form></div>);
    }

    const placeholderMessage = 'Не выбран';

    function buildStatusFilter(): JSX.Element {
        return (<Form.Field width={"3"}>
            <label>
                Статус
            </label>
            <Dropdown
                selection
                search={true}
                clearable={true}
                placeholder={placeholderMessage}
                noResultsMessage={textLabels.noResultsMessage}
                onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
                    setRequest({...request, status: data.value as TaskStatus});
                }}
                options={TaskStatusNames}
            />
        </Form.Field>);
    }

    function buildResultFilter(): JSX.Element {
        return (<Form.Field width={"3"}>
            <label>
                Результат
            </label>
            <Dropdown
                selection
                search={true}
                clearable={true}
                placeholder={placeholderMessage}
                noResultsMessage={textLabels.noResultsMessage}
                onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
                    setRequest({...request, result: data.value as TaskResult});
                }}
                options={TaskResultNames}
            />
        </Form.Field>);
    }

    function buildTaskTypeFilter(): JSX.Element {
        return (<Form.Field width={"4"}>
            <label>
                Тип задания
            </label>
            <Dropdown
                selection
                search={true}
                clearable={true}
                placeholder={placeholderMessage}
                noResultsMessage={textLabels.noResultsMessage}
                onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
                    setRequest({...request, taskTypeId: data.value?.toString()});
                }}
                options={taskTypeList.map((x) => {
                    return {value: x.id, text: x.displayName} as DropdownItemProps
                })}
            />
        </Form.Field>);
    }

    return (
        <div className={styles.table}>
            {!!task &&
                <TaskDetailsModal task={task!}
                                  onClose={() => setTask(undefined)}
                />}

            <Header>Список системных заданий</Header>

            <Button color={"teal"} icon floated={"right"} style={{marginBottom: "16px"}}
                    onClick={() => loadPage(1)}>
                <Icon name={"refresh"}/>&nbsp;&nbsp;Обновить
            </Button>

            {buildFilters()}

            <Table celled selectable striped verticalAlign='middle' color={"teal"}>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>Тип</Table.HeaderCell>
                            <Table.HeaderCell>Примечание</Table.HeaderCell>
                        <Table.HeaderCell>Создано</Table.HeaderCell>
                        <Table.HeaderCell>Кем создано</Table.HeaderCell>
                        <Table.HeaderCell>Статус</Table.HeaderCell>
                        <Table.HeaderCell>Результат</Table.HeaderCell>
                        <Table.HeaderCell>Время запуска</Table.HeaderCell>
                        <Table.HeaderCell>Время остановки</Table.HeaderCell>
                        <Table.HeaderCell>Плановое время</Table.HeaderCell>
                        <Table.HeaderCell>Файлы</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    {response?.docs.map((el, index) =>
                        <Table.Row key={`key-${index}-${el._id}`} style={{cursor: "pointer"}} onClick={() => {
                            if (!popupVisible) {
                                agent.TaskProvider.open(el._id).then((task) => setTask(task));
                            }
                        }}>
                            <Table.Cell>{el.type.displayName}</Table.Cell>
                            <Table.Cell>{el.comment}</Table.Cell>
                            <Table.Cell>{Utils.formatDateTime(el.created)}</Table.Cell>
                            <Table.Cell>{el.creator?.displayName}</Table.Cell>
                            <Table.Cell><p style={{"color": getTaskStatusColor(el.status)}}>
                                {getTaskStatusDisplayName(el.status)}</p></Table.Cell>
                            <Table.Cell><p style={{"color": getTaskResultColor(el.result)}}>
                                {getTaskResultDisplayName(el.result)}</p></Table.Cell>
                            <Table.Cell>{Utils.formatDateTime(el.startDate)}</Table.Cell>
                            <Table.Cell>{Utils.formatDateTime(el.endDate)}</Table.Cell>
                            <Table.Cell>{Utils.formatDateTime(el.job.nextRunAt)}</Table.Cell>
                            <Table.Cell
                                textAlign={"right"}>{(el.files?.length ?? 0) > 0 ?
                                <Popup flowing hoverable
                                       onOpen={() => setPopupVisible(true)}
                                       onClose={() => setPopupVisible(false)}
                                       trigger={<p style={{
                                           cursor: "pointer",
                                           color: "blue",
                                           fontWeight: "bolder"
                                       }}>{el.files!.length.toString()}</p>}>
                                    <Table celled verticalAlign='middle' color={"teal"}>
                                        <Table.Header>
                                            <Table.Row>
                                                <Table.HeaderCell>Имя файла</Table.HeaderCell>
                                                <Table.HeaderCell>Размер</Table.HeaderCell>
                                                <Table.HeaderCell>Загрузка</Table.HeaderCell>
                                            </Table.Row>
                                        </Table.Header>
                                        <Table.Body>
                                            {el.files!.map((file) => <Table.Row>
                                                <Table.Cell>{file.name}</Table.Cell>
                                                <Table.Cell>{file.size}</Table.Cell>
                                                <Table.Cell>
                                                    <Button
                                                        disabled={hasRole(UserRole.OBSERVER)}
                                                        icon onClick={(e, b) => {
                                                        Utils.openFile({fileId: file.id, download: true});
                                                    }}>
                                                        <Icon name={"download"}/>
                                                    </Button>
                                                </Table.Cell>
                                            </Table.Row>)}
                                        </Table.Body>
                                    </Table>

                                </Popup>
                                : ""}</Table.Cell>
                        </Table.Row>
                    )}
                </Table.Body>

                <Table.Footer>
                    <Table.Row>
                        <Table.HeaderCell colSpan='9'>
                            <PagingFooter page={response?.page}
                                          totalPages={response?.totalPages}
                                          nextPage={response?.nextPage}
                                          prevPage={response?.prevPage}
                                          totalDocs={response?.totalDocs}
                                          limit={response?.limit}
                                          loadPage={loadPage}/>
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Footer>
            </Table>
        </div>);
}

export default TaskList;